import { Link } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { ThemeToggle } from "@/components/ThemeToggle";
import LanguageSelector from "@/components/LanguageSelector";
import { useProduct, ProductId } from "@/hooks/useProduct";

export function GuestNavbar() {
  const { t } = useTranslation();
  const { productId, config } = useProduct();
  const [isOpen, setIsOpen] = useState(false);

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-background/95 backdrop-blur-md shadow-[0_4px_12px_-2px_rgba(0,0,0,0.15),0_2px_6px_-2px_rgba(0,0,0,0.1)] dark:shadow-[0_4px_12px_-2px_rgba(0,0,0,0.4),0_2px_6px_-2px_rgba(0,0,0,0.3)]">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2">
            <div
              className="w-9 h-9 rounded-full flex items-center justify-center"
              style={{
                background: `linear-gradient(to bottom right, ${config.branding.primaryColor}, ${config.branding.secondaryColor})`,
              }}
            >
              <span className="text-white font-bold text-lg">
                {productId === ProductId.MINDCOTINE ? "M" : "R"}
              </span>
            </div>
            <span className="text-lg font-semibold text-foreground hidden sm:inline">{config.name}</span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-1">
            <Link
              to="/#features"
              className="px-4 py-2 rounded-md text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
            >
              {t("common:nav.features", "Features")}
            </Link>
            <Link
              to="/#how-it-works"
              className="px-4 py-2 rounded-md text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
            >
              {t("common:nav.howItWorks", "How it Works")}
            </Link>
          </div>

          {/* Desktop Actions */}
          <div className="hidden lg:flex items-center gap-2">
            <LanguageSelector />
            <ThemeToggle />
            <Button asChild size="sm" className="bg-primary hover:bg-primary/90">
              <Link to="/auth">{t("common:auth.getStarted", "Get Started")}</Link>
            </Button>
          </div>

          {/* Mobile Menu Toggle */}
          <button
            className="lg:hidden p-2 rounded-md hover:bg-muted transition-colors"
            onClick={() => setIsOpen(!isOpen)}
            aria-label={t("common:nav.toggleMenu", "Toggle menu")}
          >
            {isOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        <Sheet open={isOpen} onOpenChange={setIsOpen}>
          <SheetContent side="right" className="w-80">
            <SheetHeader>
              <SheetTitle>{config.name}</SheetTitle>
            </SheetHeader>
            <div className="flex flex-col gap-4 mt-6">
              <Link
                to="/#features"
                className="block px-4 py-2 text-foreground hover:bg-muted rounded-md"
                onClick={() => setIsOpen(false)}
              >
                {t("common:nav.features", "Features")}
              </Link>
              <Link
                to="/#how-it-works"
                className="block px-4 py-2 text-foreground hover:bg-muted rounded-md"
                onClick={() => setIsOpen(false)}
              >
                {t("common:nav.howItWorks", "How it Works")}
              </Link>
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">
                  {t("common:nav.language", "Language")}:
                </span>
                <LanguageSelector />
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">
                  {t("common:nav.theme", "Theme")}:
                </span>
                <ThemeToggle />
              </div>
              <Button asChild className="bg-primary hover:bg-primary/90">
                <Link to="/auth" onClick={() => setIsOpen(false)}>
                  {t("common:auth.signIn", "Sign In")}
                </Link>
              </Button>
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </nav>
  );
}
